/**
 * A map's virtual rooms (SC-422). Each map owns one VR set, shared by every Colyseus room on it,
 * so reads go by `mapId`. Browsers can read `VirtualRoom` rows but not write them: every write
 * goes through Cloud Code (`saveVirtualRoom`, `deleteVirtualRoom`), which checks for a platform
 * admin and enforces the map rules.
 */
import Parse from '../initializeParse'
import { parseCloudRun } from '../parseCloud'
import type { TilePoint, VirtualRoomKind } from '../../../../types/Maps'

export interface VirtualRoomData {
    id: string
    /** The VR id (`VirtualRoom.virtualRoomId`), which bookings and tiles refer to. */
    roomId: string
    mapId: string
    kind: VirtualRoomKind
    name: string
    color: string
    properties: {
        uniqueRoomId: string
        proximityDistance: number
    }
    bounds?: {
        x: number
        y: number
        width: number
        height: number
    }
    metadata?: {
        description?: string
        capacity?: number
        /** Where arrivals land, in tiles, just outside the VR. */
        spawnPoint?: TilePoint
        [key: string]: unknown
    }
    createdBy?: string
    lastModifiedBy?: string
    createdAt?: Date
    updatedAt?: Date
}

/** A create or update. The editor generates `roomId` for a new VR (plan D10). */
export interface VirtualRoomSaveData {
    mapId: string
    roomId: string
    name?: string
    color?: string
    properties?: { uniqueRoomId?: string; proximityDistance?: number }
    bounds?: VirtualRoomData['bounds'] | null
    metadata?: { description?: string | null; capacity?: number | null; spawnPoint?: TilePoint | null }
    kind?: 'meeting' | 'cubicle'
}

export interface SyncResult {
    success: boolean
    syncedCount: number
    errorCount: number
    errors: string[]
}

/** Only these metadata keys can be saved; Cloud Code keeps the rest of a VR's metadata. */
function editableMetadata(metadata: Record<string, any> | undefined) {
    if (!metadata) return undefined
    const out: Record<string, unknown> = {}
    for (const key of ['description', 'capacity', 'spawnPoint']) {
        if (key in metadata) out[key] = metadata[key]
    }
    return out
}

export class VirtualRoomService {
    async getRoomsByMapId(mapId: string): Promise<VirtualRoomData[]> {
        if (!mapId) return []
        const query = new Parse.Query('VirtualRoom')
        query.equalTo('mapId', mapId)
        query.limit(1000)
        const rows = await query.find()
        return rows.map((row: any) => this.parseToVirtualRoomData(row))
    }

    async getRoomById(roomId: string): Promise<VirtualRoomData | null> {
        const query = new Parse.Query('VirtualRoom')
        query.equalTo('virtualRoomId', roomId)
        const row = await query.first()
        return row ? this.parseToVirtualRoomData(row) : null
    }

    /** Creates the VR, or updates it when it exists (`saveVirtualRoom` does both). */
    async saveRoom(data: VirtualRoomSaveData): Promise<VirtualRoomData> {
        const params: Record<string, unknown> = { mapId: data.mapId, virtualRoomId: data.roomId }
        for (const key of ['name', 'color', 'properties', 'bounds', 'kind'] as const) {
            if (data[key] !== undefined) params[key] = data[key]
        }
        const metadata = editableMetadata(data.metadata as Record<string, any> | undefined)
        if (metadata) params.metadata = metadata
        const saved = await parseCloudRun<any>('saveVirtualRoom', params)
        return {
            id: saved.virtualRoomId,
            roomId: saved.virtualRoomId,
            mapId: saved.mapId,
            kind: saved.kind,
            name: saved.name,
            color: saved.color,
            properties: { uniqueRoomId: saved.virtualRoomId, proximityDistance: 100, ...saved.properties },
            bounds: saved.bounds ?? undefined,
            metadata: saved.metadata,
        }
    }

    createRoom(data: VirtualRoomSaveData): Promise<VirtualRoomData> {
        return this.saveRoom(data)
    }

    updateRoom(data: VirtualRoomSaveData): Promise<VirtualRoomData> {
        return this.saveRoom(data)
    }

    /** Deletes the VR and its tiles. Refused while any company has upcoming bookings in it. */
    async deleteRoom(roomId: string, mapId: string): Promise<void> {
        await parseCloudRun('deleteVirtualRoom', { mapId, virtualRoomId: roomId })
    }

    private parseToVirtualRoomData(row: any): VirtualRoomData {
        return {
            id: row.id,
            roomId: row.get('virtualRoomId'),
            mapId: row.get('mapId'),
            kind: row.get('kind') || 'meeting',
            name: row.get('name'),
            color: row.get('color'),
            properties: { uniqueRoomId: row.get('virtualRoomId'), proximityDistance: 100, ...row.get('properties') },
            bounds: row.get('bounds'),
            metadata: row.get('metadata'),
            createdBy: row.get('createdBy'),
            lastModifiedBy: row.get('lastModifiedBy'),
            createdAt: row.createdAt,
            updatedAt: row.updatedAt,
        }
    }
}

export const virtualRoomService = new VirtualRoomService()
